import { BLOG_CONTENT_TYPE, BLOG_FILTERS_KEYS, PAGE_PATH_HOME } from '@bluheadless/ui-logic/src/constants'
import { columnsConversion } from '@bluheadless/ui-logic/src/utils/commons'
import { getCmsFilters } from '@bluheadless/ui-logic/src/cms/filters'
import { useCmsBlocks } from '@bluheadless/ui-logic/src/hooks/cms/useCmsBlocks'
import { useCmsContents } from '@bluheadless/ui-logic/src/hooks/cms/useCmsContents'
import { usePagination } from '@bluheadless/ui-logic/src/hooks/usePagination'
import { useCmsWhereAnd } from '@bluheadless/ui-logic/src/hooks/cms/useCmsWhereAnd'
import { useCmsFilters } from '@bluheadless/ui-logic/src/hooks/cms/useCmsFilters'
import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { name as laggySwrMiddlewareName } from '@bluheadless/ui-logic/src/stale-while-revalidate/middlewares/laggy'
import { Grid } from '@mui/material'
import { cx } from '@emotion/css'
import capitalize from 'lodash/capitalize'
import isEqual from 'lodash/isEqual'
import { useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { FormattedMessage, useIntl } from 'react-intl'
import Button from '../../molecules/button'
import SkeletonCard from '../../molecules/skeleton/card/skeleton-card'
import BlogFilters from '../../organisms/blog/filters'
import BlogItems from '../../organisms/blog/items'
import Block from '../../particles/cms-block/block'
import { BlogWrapper, Breadcrumbs, Content, Heading, Pagination } from './blog.styled'
import Progress from '../../atoms/progress'

const BlogPageComponent = ({
	activeFilters: defaultActiveFilters,
	filters: defaultFilters,
	page: defaultPage,
	lastPage: defaultLastPage,
	order: defaultOrder,
	whereAnd: defaultWhereAnd,
}) => {
	const { formatMessage } = useIntl()
	const {
		blog: {
			path,
			breadcrumbs: breadcrumbsEnabled,
			heading: headingEnabled,
			template,
			view,
			columns: _columns,
			itemsPerPage: defaultLimit,
			cmsBlockIdentifierTop,
			cmsBlockIdentifierBottom,
		},
	} = useConfig()

	const breadcrumbsItems = [
		{ title: formatMessage({ id: 'breadcrumbs_home' }), url: PAGE_PATH_HOME },
		{ title: formatMessage({ id: 'blog_page_title' }), url: path },
	]
	const resettingNavigation = useRef(false)
	const rootRef = useRef(null)

	const [filters, setFilters] = useState(defaultFilters || {})

	// variables for rapid checking of template
	const isPagination = template === 'pagination'
	const isInfinite = template === 'infinite'
	const isInfiniteScroll = template === 'infinite-scroll'

	const { blocks: cmsBlocks } = useCmsBlocks({
		identifiers: [cmsBlockIdentifierTop, cmsBlockIdentifierBottom],
	})

	const cmsBlockContentTop = cmsBlocks?.find((block) => block?.identifier === cmsBlockIdentifierTop)?.content || null
	const cmsBlockContentBottom =
		cmsBlocks?.find((block) => block?.identifier === cmsBlockIdentifierBottom)?.content || null

	const { activeFilters, onApplyFilter, onDeleteFilter, onDeleteAllFilters } = useCmsFilters({
		defaultActiveFilters,
		queryParamsToInclude: BLOG_FILTERS_KEYS,
		validFilters: filters,
	})

	const whereAnd = useCmsWhereAnd({ type: BLOG_CONTENT_TYPE, defaultWhereAnd, activeFilters })

	const { onLoadPrev, onLoadNext, onLoadPage, onLastPageChange, currentPage, prevPage, lastPage, lastPageReached } =
		usePagination({
			defaultCurrentPage: defaultPage > 1 ? defaultPage : undefined,
			defaultLastPage,
		})

	// get all blog data to create the filters - missing API call to get this in a more efficient way
	const { contents: allData, loading: loadingAllData } = useCmsContents({
		type: BLOG_CONTENT_TYPE,
		pausedCount: true,
	})

	if (currentPage === 1) {
		resettingNavigation.current = false
	}

	const { contents, count, loading } = useCmsContents({
		type: BLOG_CONTENT_TYPE,
		order: defaultOrder,
		pagination: {
			page: currentPage,
			itemsPerPage: defaultLimit,
			infinite: isInfinite || isInfiniteScroll,
		},
		whereAnd,
		optionsCount: {
			bhUse: [laggySwrMiddlewareName],
		},
		paused: resettingNavigation.current,
	})

	// Used only if template is infinite-scroll
	const [inViewRef, inView] = useInView({
		threshold: 0,
		rootMargin: '400px',
		triggerOnce: false,
		// Skip creating the IntersectionObserver. This is only needed for infinite-scroll template.
		skip: !isInfiniteScroll,
	})

	// convert columns values to work with Grid.
	// e.g. if columns is 1, then we need to pass `columns: 12` to Grid.
	const columns = columnsConversion({ columns: _columns, humanFriendly: false })

	const skeletonProps = {
		orientation: 'vertical',
		rows: 1,
		textRowsNumber: 3,
		cardRatio: 0.8,
		responsiveProps: { columns: columns },
	}

	useEffect(() => {
		// do not trigger if contents are already loading.
		// prevents infinite scroll from triggering on page load
		if (inView && !loading) {
			onLoadNext()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inView, loading])

	useEffect(() => {
		if (count !== undefined && currentPage) {
			onLastPageChange(Math.ceil(count / defaultLimit) || 1, currentPage)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [count, defaultLimit])

	useEffect(() => {
		if (allData?.length > 0 && !loadingAllData) {
			const cmsFilters = getCmsFilters({
				type: BLOG_CONTENT_TYPE,
				keysToFilter: BLOG_FILTERS_KEYS,
				dataToFilter: allData,
			})
			if (!isEqual(filters, cmsFilters)) setFilters(cmsFilters)
		}
	}, [allData, loadingAllData, filters])

	return (
		<BlogWrapper>
			{breadcrumbsEnabled && <Breadcrumbs items={breadcrumbsItems} />}
			{headingEnabled && (
				<Heading component="h1" variant="h1">
					{formatMessage({ id: 'blog_page_title' })}
				</Heading>
			)}
			{!!cmsBlockContentTop && <Block content={cmsBlockContentTop} />}
			<div className={cx(`Blog-${capitalize(template)}-root`)} ref={rootRef}>
				<BlogFilters
					activeFilters={activeFilters}
					filters={filters}
					onApplyFilter={(...args) => {
						resettingNavigation.current = true
						onApplyFilter(...args)
						onLoadPage(1, rootRef)
					}}
					onDeleteFilter={(...args) => {
						resettingNavigation.current = true
						onDeleteFilter(...args)
						onLoadPage(1, rootRef)
					}}
					onDeleteAllFilters={(...args) => {
						resettingNavigation.current = true
						onDeleteAllFilters(...args)
						onLoadPage(1, rootRef)
					}}
				/>
				<Content viewMode={view}>
					{loading && !contents?.length > 0 && <SkeletonCard {...skeletonProps} />}
					<Grid container spacing={0}>
						{contents?.length > 0 && (
							<>
								{(isInfinite || isInfiniteScroll) && prevPage > 0 && (
									<Grid item xs={12} textAlign="center">
										<Button onClick={onLoadPrev} disabled={loading} loading={loading}>
											<FormattedMessage id="load_prev" />
										</Button>
									</Grid>
								)}
								<Grid item xs={12}>
									<BlogItems items={contents} columns={_columns} viewMode={view} />
								</Grid>
								{(isInfinite || isInfiniteScroll) && !lastPageReached && (
									<Grid item xs={12} textAlign="center" mt={3} ref={inViewRef}>
										{isInfinite && (
											<Button onClick={onLoadNext} disabled={loading} loading={loading}>
												<FormattedMessage id="load_next" />
											</Button>
										)}
									</Grid>
								)}
							</>
						)}
						{isPagination && lastPage > 1 && (
							<Grid item xs={12} mt={4}>
								<Pagination
									count={Math.ceil(count / defaultLimit) || lastPage}
									page={currentPage}
									onChange={(e, page) => onLoadPage(page, rootRef)}
									disabled={loading}
									hideNextButton={false}
									hidePrevButton={false}
									showFirstButton={false}
									showLastButton={false}
								/>
							</Grid>
						)}
						{loading && contents?.length > 0 && (
							<Grid item xs={12} textAlign="center" sx={{ padding: '25px 0px' }}>
								<Progress />
							</Grid>
						)}
					</Grid>
				</Content>
			</div>
			{!!cmsBlockContentBottom && <Block content={cmsBlockContentBottom} />}
		</BlogWrapper>
	)
}

BlogPageComponent.defaultProps = {}

BlogPageComponent.propTypes = {}

export default BlogPageComponent
